<script setup>
/**
 * One deal as a board card: what it is, who it is with, what it is worth, and
 * who owns it.
 *
 * The card renders no stage of its own — the column it sits in is the stage —
 * and it is the column's unit of drag. There is no deal workroom yet, so the
 * card is not a link: it says everything it has to say where it stands.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  opportunity: {
    type: Object,
    required: true,
  },
});

const AVATAR_SIZE = 20;

// Well-formed is all Intl asks for, and all we can promise: the column is three
// characters the account typed, not a code any list here validates.
const isWellFormedCurrency = currency => /^[a-z]{3}$/i.test(currency || '');

const { t } = useI18n();

// A deal worth nothing is a deal nobody has priced yet, so the figure is left
// off rather than shown as a zero the board would read as a lost sale.
const amount = computed(() => {
  const value = Number(props.opportunity.value);
  if (!value) return '';

  const { currency } = props.opportunity;
  return new Intl.NumberFormat(
    undefined,
    isWellFormedCurrency(currency)
      ? { style: 'currency', currency, maximumFractionDigits: 0 }
      : { maximumFractionDigits: 0 }
  ).format(value);
});

// `assignee` is an empty object rather than null when nobody owns the deal, so
// the id is what "assigned" means here.
const assignee = computed(() =>
  props.opportunity.assignee?.id ? props.opportunity.assignee : null
);

const contactName = computed(() => props.opportunity.contact?.name || '');
</script>

<template>
  <article
    class="flex flex-col gap-2 p-3 transition-colors border rounded-lg bg-n-solid-2 border-n-weak hover:border-n-strong"
    :data-testid="`opportunity-board-card-${opportunity.id}`"
  >
    <h4 class="min-w-0 mb-0 text-sm font-medium text-n-slate-12 line-clamp-2">
      {{ opportunity.name }}
    </h4>

    <div class="flex items-center gap-2">
      <span v-if="contactName" class="min-w-0 text-xs truncate text-n-slate-11">
        {{ contactName }}
      </span>
      <span
        v-if="amount"
        class="text-xs font-medium tabular-nums ltr:ml-auto rtl:mr-auto whitespace-nowrap text-n-slate-12"
        data-testid="opportunity-board-card-value"
      >
        {{ amount }}
      </span>
    </div>

    <span
      class="inline-flex items-center min-w-0 gap-1.5"
      :aria-label="
        assignee
          ? t('OPPORTUNITIES.BOARD.ASSIGNEE_ARIA', { name: assignee.name })
          : t('OPPORTUNITIES.BOARD.UNASSIGNED')
      "
    >
      <span
        v-if="!assignee"
        class="flex items-center justify-center rounded-md size-5 text-n-slate-10 bg-n-alpha-2"
      >
        <Icon icon="i-lucide-circle-dashed" class="size-3" />
      </span>
      <Avatar
        v-else
        :name="assignee.name"
        :src="assignee.thumbnail"
        :size="AVATAR_SIZE"
      />
      <span
        class="text-xs truncate"
        :class="assignee ? 'text-n-slate-11' : 'text-n-slate-10'"
      >
        {{ assignee ? assignee.name : t('OPPORTUNITIES.BOARD.UNASSIGNED') }}
      </span>
    </span>
  </article>
</template>
